'use client';

import { Popover as BasePopover } from '@base-ui/react/popover';

import { type ZeusHTMLProps } from '../../internal/types/html-props';
import { type Component, type ComponentProps } from '../../types/component';
import { usePopoverContext } from './internal/PopoverContext';

interface PopoverTriggerBaseProps {
  /**
   * Function that renders the trigger for the Popover. It receives `props` and `state` as parameters.
   *
   * @param props - The props to apply to the trigger element for the Popover.
   * @param state.isOpen - Whether the Popover is open or closed.
   *
   * @return The trigger element.
   */
  render: (
    props: ZeusHTMLProps,
    state: { readonly isOpen: boolean }
  ) => React.ReactElement;
  /**
   * Open the popover when the trigger is hovered.
   *
   * @default false
   */
  enableHover?: boolean;
}

type PopoverTriggerProps = ComponentProps<PopoverTriggerBaseProps>;

type PopoverTriggerComponent = Component<PopoverTriggerBaseProps>;

const PopoverTrigger: PopoverTriggerComponent = (props) => {
  const ctx = usePopoverContext();
  const { render, enableHover = ctx.enableHover ?? false } = props;

  return (
    <BasePopover.Trigger
      openOnHover={enableHover}
      render={(baseProps, baseState) =>
        render(baseProps as ZeusHTMLProps, { isOpen: baseState.open })
      }
    />
  );
};

PopoverTrigger.displayName = 'Popover.Trigger';

export {
  PopoverTrigger,
  type PopoverTriggerProps,
  type PopoverTriggerBaseProps,
};
